/**
 * The photoelectric effect: KEmax = hf − φ, the stopping potential, the threshold
 * frequency and wavelength, and Millikan's method of finding h from stopping potentials.
 *
 * h and e are exact SI values. Work functions are from the CRC Handbook of Chemistry and
 * Physics (the 2008 edition's table of work functions, p. 12-124, as tabulated in
 * Wikipedia's "Work function"). The handbook gives a range for metals whose value depends
 * on the crystal face and surface condition; the lowest value of the range is used here.
 */

import {
  ELEMENTARY_CHARGE,
  PLANCK,
  SPEED_OF_LIGHT,
} from "@/lib/tool-kit/physics-waves/electromagnetic";

/** Electron rest mass, kg (CODATA 2022). */
export const ELECTRON_MASS = 9.109_383_713_9e-31;

export type MetalId =
  | "cs"
  | "rb"
  | "k"
  | "na"
  | "ca"
  | "li"
  | "mg"
  | "zn"
  | "ti"
  | "pb"
  | "al"
  | "ag"
  | "w"
  | "fe"
  | "cu"
  | "ni"
  | "au"
  | "pt"
  | "be";

export interface Metal {
  id: MetalId;
  symbol: string;
  /** Work function in eV used by the calculator. */
  phi: number;
  /** The handbook's upper value, where it lists a range. */
  phiHigh?: number;
}

export const METALS: readonly Metal[] = [
  { id: "cs", symbol: "Cs", phi: 1.95 },
  { id: "rb", symbol: "Rb", phi: 2.261 },
  { id: "k", symbol: "K", phi: 2.29 },
  { id: "na", symbol: "Na", phi: 2.75 },
  { id: "ca", symbol: "Ca", phi: 2.87 },
  { id: "li", symbol: "Li", phi: 2.9 },
  { id: "mg", symbol: "Mg", phi: 3.66 },
  { id: "zn", symbol: "Zn", phi: 3.63, phiHigh: 4.9 },
  { id: "ti", symbol: "Ti", phi: 4.33 },
  { id: "pb", symbol: "Pb", phi: 4.25 },
  { id: "al", symbol: "Al", phi: 4.06, phiHigh: 4.26 },
  { id: "ag", symbol: "Ag", phi: 4.26, phiHigh: 4.74 },
  { id: "w", symbol: "W", phi: 4.32, phiHigh: 4.55 },
  { id: "fe", symbol: "Fe", phi: 4.67, phiHigh: 4.81 },
  { id: "cu", symbol: "Cu", phi: 4.53, phiHigh: 5.1 },
  { id: "ni", symbol: "Ni", phi: 5.04, phiHigh: 5.35 },
  { id: "au", symbol: "Au", phi: 5.1, phiHigh: 5.47 },
  { id: "pt", symbol: "Pt", phi: 5.12, phiHigh: 5.93 },
  { id: "be", symbol: "Be", phi: 4.98 },
];

export interface Emission {
  /** True when the photon energy exceeds the work function. */
  emitted: boolean;
  /** Maximum kinetic energy of the ejected electrons, J (0 when none is emitted). */
  kineticJoules: number;
  kineticEv: number;
  /** Stopping potential in volts: KEmax ÷ e. */
  stoppingVolts: number;
  /** Fastest electron speed, m/s, non-relativistic. */
  speed: number;
  /** Photon energy minus work function, eV: negative when the light is too weak. */
  excessEv: number;
}

/** KEmax = E_photon − φ for photon energy and work function in joules. */
export function photoemission(
  photonJoules: number,
  phiJoules: number
): Emission {
  const excess = photonJoules - phiJoules;
  const kinetic = Math.max(excess, 0);
  return {
    emitted: excess > 0,
    kineticJoules: kinetic,
    kineticEv: kinetic / ELEMENTARY_CHARGE,
    stoppingVolts: kinetic / ELEMENTARY_CHARGE,
    speed: Math.sqrt((2 * kinetic) / ELECTRON_MASS),
    excessEv: excess / ELEMENTARY_CHARGE,
  };
}

export interface Threshold {
  /** Hz */
  frequency: number;
  /** m */
  wavelength: number;
}

/** The lowest frequency (longest wavelength) that frees an electron: f₀ = φ ÷ h. */
export function threshold(phiJoules: number): Threshold {
  return {
    frequency: phiJoules / PLANCK,
    wavelength: (PLANCK * SPEED_OF_LIGHT) / phiJoules,
  };
}

export interface LineFit {
  slope: number;
  intercept: number;
  /** Coefficient of determination; 1 for a perfect line (or exactly two points). */
  r2: number;
}

/** Ordinary least-squares line through points; null for fewer than two or a vertical set. */
export function linearFit(
  points: readonly (readonly [number, number])[]
): LineFit | null {
  const n = points.length;
  if (n < 2) {
    return null;
  }
  const mx = points.reduce((s, [x]) => s + x, 0) / n;
  const my = points.reduce((s, [, y]) => s + y, 0) / n;
  const sxx = points.reduce((s, [x]) => s + (x - mx) ** 2, 0);
  const sxy = points.reduce((s, [x, y]) => s + (x - mx) * (y - my), 0);
  const syy = points.reduce((s, [, y]) => s + (y - my) ** 2, 0);
  if (sxx === 0) {
    return null;
  }
  const slope = sxy / sxx;
  const r2 = syy === 0 ? 1 : (sxy * sxy) / (sxx * syy);
  return { slope, intercept: my - slope * mx, r2 };
}

export interface Millikan {
  /** Planck constant from the slope, J·s: h = e × (ΔV_s ÷ Δf). */
  h: number;
  /** Work function from the intercept, eV: φ = −e × intercept ÷ e. */
  phiEv: number;
  /** Threshold frequency where the line crosses zero, Hz. */
  thresholdFrequency: number;
  r2: number;
  fit: LineFit;
}

/** Planck's constant and the work function from (frequency Hz, stopping potential V) pairs. */
export function millikan(
  points: readonly (readonly [number, number])[]
): Millikan | null {
  const fit = linearFit(points);
  if (fit === null || !(fit.slope > 0)) {
    return null;
  }
  return {
    h: ELEMENTARY_CHARGE * fit.slope,
    phiEv: -fit.intercept,
    thresholdFrequency: -fit.intercept / fit.slope,
    r2: fit.r2,
    fit,
  };
}

/** The listed metal whose work function is closest to `phiEv`. */
export function closestMetal(phiEv: number): Metal {
  return METALS.reduce((best, metal) =>
    Math.abs(metal.phi - phiEv) < Math.abs(best.phi - phiEv) ? metal : best
  );
}
